import type { TurnstileEvent } from '@kompare/captcha-react';
import apm from 'src/apm';

/** Where in the funnel the widget lives; a label on every event. */
export type CaptchaPage = 'basic-data' | 'passenger-data';

/**
 * What the visitor submitted, reduced to what is safe to send to APM: never the email
 * itself (PII), only whether one was given and how long it is. Label names match the
 * other funnels' (`input_kind`, `input_length`) so cross-funnel dashboards line up.
 */
export type CaptchaInput = {
  kind: 'email' | 'empty';
  length: number;
};

export function describeInput(value: string | null | undefined): CaptchaInput {
  const trimmed = (value ?? '').trim();
  return { kind: trimmed.length ? 'email' : 'empty', length: trimmed.length };
}

// Every captcha lifecycle event (submit 428 → captcha-required → rendered → token →
// submit <status>) is its own unmanaged transaction `turnstile <event>` of type
// `captcha`; widget failures are additionally APM errors. Events of one challenge share
// `labels.challenge_id` — the RUM agent stamps `session.id` only on the page-load
// transaction, never on custom ones, so this is the only way to stitch them in Kibana.
// No-op without apm (local development) and never throws.
//
// Not apm.startSpan()/addLabels(): without an active transaction startSpan() opens a
// temporary transaction the agent drops, and addLabels() writes global context that
// every later event would inherit.
//
// Copied from dzo-webshop (`src/common/services/captchaTelemetry.service.tsx`) with the
// app-owned `describeInput` rewritten for this funnel's email input.

const FAILURES = new Set<TurnstileEvent['type']>(['script-failed', 'error', 'unsupported', 'timeout']);

type Labels = Record<string, string | number | boolean>;

function labelsFor(page: CaptchaPage, event: string, code?: string, extra: Labels = {}): Labels {
  return {
    page,
    turnstile_event: event,
    turnstile_code: code ?? '',
    ...extra,
  };
}

function inputLabels(input?: CaptchaInput): Labels {
  if (!input) return {};
  return { input_kind: input.kind, input_length: input.length };
}

function challengeLabel(challengeId?: string): Labels {
  return challengeId ? { challenge_id: challengeId } : {};
}

/** A fresh correlation id for one challenge. */
export function newChallengeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function record(page: CaptchaPage, event: string, code?: string, extra: Labels = {}) {
  if (!apm) return;
  try {
    const labels = labelsFor(page, event, code, extra);
    const tr = apm.startTransaction(`turnstile ${event}`, 'captcha', { managed: false });
    tr?.addLabels(labels);
    // The agent discards 0 ms transactions, and start→end in one tick rounds to 0.
    tr?.end(performance.now() + 1);
    if (FAILURES.has(event as TurnstileEvent['type'])) {
      const err = new Error(`turnstile ${event}${code ? ` ${code}` : ''}`);
      err.name = 'TurnstileError';
      apm.captureError(err, { labels });
    }
  } catch {
    // never let telemetry surface to the user
  }
}

/** The server answered CAPTCHA_REQUIRED and the widget is about to be armed. */
export function reportCaptchaRequired(page: CaptchaPage, challengeId: string, input?: CaptchaInput) {
  record(page, 'captcha-required', undefined, { ...challengeLabel(challengeId), ...inputLabels(input) });
}

/** Wire to `<TurnstileWidget onEvent>`. */
export function reportTurnstileEvent(page: CaptchaPage, event: TurnstileEvent, challengeId?: string) {
  record(page, event.type, event.type === 'error' ? event.code : undefined, challengeLabel(challengeId));
}

/**
 * Outcome of every createSo submit (`status` from `submitStatusOf()`, 200 on success).
 * `with_token` tells "solved, then rejected again" apart from a plain abandon.
 */
export function reportSubmitOutcome(
  page: CaptchaPage,
  status: number,
  input: CaptchaInput,
  withToken: boolean,
  challengeId?: string
) {
  record(page, 'submit', undefined, {
    ...challengeLabel(challengeId),
    ...inputLabels(input),
    submit_status: status,
    with_token: withToken,
  });
}
